import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '@components/Layout/Layout';
import { useState, useMemo } from 'react';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import ChartContainer from '@/components/Chart/ChartContainer.tsx';
import { getDatePlusMinus } from '@einhaus/meteoric-helpers';
import EntityLink from '@/components/Entity/EntityLink.tsx';

const AssetOption = () => {
    const { id, optionId } = useParams<{ id: string; optionId: string; entityType: string; derivativeType: string }>();
    const [searchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);

    const startDateTimeParam = searchParams.get('startDateTime') ? searchParams.get('startDateTime')! : null;
    const endDateTimeParam = searchParams.get('endDateTime') ? searchParams.get('endDateTime')! : null;

    // Define query parameters
    const entityParams = useMemo(() => {
        return id ? { entityId: +id, entityType: 'Asset' as const } : null;
    }, [id]);

    const optionParams = useMemo(() => {
        return id && optionId ? { entityId: +id, entityType: 'Asset' as const, optionId: +optionId } : null;
    }, [id, optionId]);

    // Fetch entity details
    const entityQuery = useAppQuery(
        {
            ...trpc.entity.details.queryOptions(entityParams || { entityId: 0, entityType: 'Asset' }),
            enabled: !!entityParams
        },
        {
            fallbackErrorMessage: 'Failed to load Asset data',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Fetch option details
    const optionQuery = useAppQuery(
        {
            ...trpc.entity.fetchOption.queryOptions(optionParams || { entityId: 0, entityType: 'Asset', optionId: 0 }),
            enabled: !!optionParams
        },
        {
            fallbackErrorMessage: 'Failed to fetch option',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Extract data from queries
    const entity = entityQuery.data?.entity;
    const option = optionQuery.data?.option;

    // Calculate date range from URL params, falling back to the option's expiration once loaded
    const startDateTime =
        startDateTimeParam ?? (option ? getDatePlusMinus('-', 30, 'day', 'ymdhms', 'start', option.expirationDate) : null);

    const endDateTime = endDateTimeParam ?? (option ? getDatePlusMinus('+', 1, 'day', 'ymdhms', 'end', option.expirationDate) : null);

    // Handle loading states
    const isLoading = entityQuery.isLoading || optionQuery.isLoading;

    if (isLoading) {
        return <div>Loading...</div>;
    }

    // Handle error states
    if (error) {
        return <div className="text-red-500">{error}</div>;
    }

    // Handle missing data
    if (!entity) {
        return <div>Entity not found</div>;
    }

    if (!option) {
        return <div>Option not found</div>;
    }

    if (!startDateTime || !endDateTime) {
        return <div>Start and end date are required</div>;
    }

    return (
        <Layout title="Asset Option - Project 314">
            <div className="w-full">
                <div className="w-full">
                    <h3 className="text-3xl font-bold mb-0">
                        <EntityLink showName showSymbol entity={entity} />
                    </h3>
                    <h3 className="text-zinc-500 mb-3 text-lg">
                        {option.symbol} {option.description}{' '}
                    </h3>
                </div>
                <div className="w-full min-h-[500px] h-auto mb-8" style={{ display: 'flex', flexDirection: 'column' }}>
                    <ChartContainer
                        duration="1m"
                        endDateTime={endDateTime}
                        entity={entity}
                        option={option}
                        shouldIncludeVolumeSeries={false}
                        startDateTime={startDateTime}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default AssetOption;
